import React, {useEffect, useRef, useState} from "react";
import _ from "lodash";
import {bindActionCreators} from "redux";
import {connect} from "react-redux";
import {push} from "react-router-redux";
import setTitle from "utils/set_title";
import DataAssetsCreateView, {
  DefinitionConfig,
} from "../components/data_assets_create_view";
import requestor from "requestor";
import addWorkflowInstance from "modules/flowmaster/actions/workflow_instance_add";
import updateWorkflowInstanceName from "modules/flowmaster/actions/workflow_instance_update_name";
import updateWorkflowInstanceIsArchived from "modules/flowmaster/actions/workflow_instance_update_is_archived";
import updateWorkflowInstanceIsPublic from "modules/flowmaster/actions/workflow_instance_update_is_public";
import updateWorkflowInstanceDefaultAssetName from "modules/flowmaster/actions/workflow_instance_update_default_asset_name";
import runGenerateDataAsset from "modules/flowmaster/actions/data_asset_run";
import fetchDataAssetConfigRuns from "modules/flowmaster/actions/data_asset_config_runs_fetch";
import addTestAction from "modules/flowmaster/actions/workflow_test_add";
import updateTestAction from "modules/flowmaster/actions/workflow_test_update";
import deleteTestAction from "modules/flowmaster/actions/workflow_test_delete";
import rerunTestsAction from "modules/flowmaster/actions/llm_run_rerun_tests";
import rerunAllTestsAction from "modules/flowmaster/actions/llm_run_rerun_multiple_tests";
import getTestResultsAction from "modules/flowmaster/actions/workflow_test_results_fetch";
import llmRunFetchAction from "modules/flowmaster/actions/llm_run_fetch";
import clearLlmRuns from "modules/flowmaster/actions/llm_runs_clear";
import {DataAssetReference} from "../components/configuration_inputs";
import {
  DataKey,
  WorkflowInstanceConfig,
} from "common/flowmaster/types/workflow";
import {TestId} from "common/flowmaster/types/test";
import {LlmRunId} from "common/flowmaster/types/llm_run";
import {DataAssetRunInfo} from "common/flowmaster/types/data_asset";
import updateUrlParamsWithDispatch from "routes/data_assets/util/update_url_params_with_dispatch";

const REFRESH_INTERVAL_MS = 5000;

export const getQueryParams = (additionalOptions?: object) => {
  const searchParams = new URLSearchParams(location.search);
  if (additionalOptions) {
    for (const [key, value] of Object.entries(additionalOptions)) {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString().replace(/%3A/gi, ":");
  return query ? `?${query}` : "";
};

export const isDataAsset = (value: unknown): value is DataAssetReference =>
  value !== null &&
  typeof value === "object" &&
  "definition_id" in value &&
  "config_id" in value &&
  "run_id" in value &&
  "output_key" in value;

export function processConfigInputs(
  config: WorkflowInstanceConfig,
): WorkflowInstanceConfig {
  if (!config.inputs?.values) {
    return config;
  }

  const cleanedValues = Object.fromEntries(
    Object.entries(config.inputs.values).map(([key, val]) => {
      if (isDataAsset(val)) {
        const {
          definition_group_id,
          definition_id,
          config_group_id,
          config_id,
          run_id,
          output_key,
        } = val;

        return [
          key,
          {
            definition_group_id: definition_group_id || null,
            definition_id: definition_id || null,
            config_group_id: config_group_id || null,
            config_id: config_id || null,
            run_id: run_id || null,
            output_key,
          },
        ];
      }
      // stringify schema inputs before they are sent to action
      if (_.isPlainObject(val) && _.get(val, "parameters")) {
        return [key, JSON.stringify(val)];
      }

      return [key, val];
    }),
  );

  return {
    ...config,
    inputs: {
      ...config.inputs,
      values: cleanedValues,
    },
  };
}

function DataAssetsCreateViewContainer(props) {
  const canFetchRuns = () => {
    return props.params.definitionId && props.params.configId;
  };
  const [loadingConfigRuns, setLoadingConfigRuns] = useState(canFetchRuns());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const autoRefreshEnabled = props.autoRefreshEnabled;

  const [rerunningTests, setRerunningTests] = useState(false);
  const [refreshingRun, setRefreshingRun] = useState(false);

  const [fetchConfigRunsError, setFetchConfigRunsError] = useState<
    string | null
  >(null);

  const searchParams = new URLSearchParams(props.location.search);
  const projectId = searchParams.get("project");
  const documentId = searchParams.get("document");
  const from = searchParams.get("from") ?? null;
  const to = searchParams.get("to") ?? null;
  const maxRuns = searchParams.get("max_runs") ?? 100;

  const {selectedLlmRun} = props;

  const configRunsRef = useRef(props.configRuns);

  const updateValues = pairs =>
    updateUrlParamsWithDispatch(pairs, props.dispatch);

  useEffect(() => {
    configRunsRef.current = props.configRuns;
  }, [props.configRuns]);

  const [showGroupRuns, setShowGroupRuns] = useState<boolean>(false);

  function handleShowGroupRunsChecked(value: boolean) {
    setShowGroupRuns(value);
    fetchConfigRuns(false, value);
  }

  async function fetchConfigRuns(
    refresh = false,
    fetchGroupRuns = showGroupRuns,
  ) {
    const {organisationId, definitionId, configId, assetId} = props.params;

    setFetchConfigRunsError(null);
    setIsRefreshing(true);
    if (!refresh) {
      setLoadingConfigRuns(true);
    }

    try {
      await props.fetchDataAssetConfigRuns(
        organisationId,
        projectId,
        documentId,
        definitionId,
        configId,
        from,
        to,
        fetchGroupRuns,
        assetId,
        maxRuns,
      );
    } catch (error) {
      setFetchConfigRunsError("Failed to load runs for this configuration");
    } finally {
      setLoadingConfigRuns(false);
      setIsRefreshing(false);
    }
  }

  async function checkAndRefreshRuns() {
    const configRuns = configRunsRef.current;
    if (!canFetchRuns() || !Array.isArray(configRuns)) {
      return;
    }

    const nonPendingRuns = configRuns.filter(
      (run: DataAssetRunInfo) => !("pending" in run),
    ) as DataAssetRunInfo[];
    const incompleteRuns = nonPendingRuns.filter(
      run => !run.is_complete && !run.has_failed,
    );

    if (incompleteRuns.length > 0) {
      fetchConfigRuns(true);
    }
  }

  async function clearConfigRuns() {
    clearAssetPath();
    clearLlmRuns();
  }

  function clearAssetPath() {
    const currentPath = props.location.pathname;
    const newPath = currentPath.replace(/\/asset\/\d+/, "");
    props.push(`${newPath}${getQueryParams()}`);
  }

  async function createDefinitionConfig(
    config,
    name,
    groupId,
    archive = false,
  ) {
    const {organisationId, definitionId} = props.params;
    const updatedConfig = processConfigInputs(config);

    const result = await props.addDefinitionConfig(
      organisationId,
      definitionId,
      name,
      updatedConfig,
      groupId,
      archive && props.params.configId,
    );

    const newConfigId = result.value.instance.id;
    const currentPath = props.location.pathname;

    let newPath: string;

    if (currentPath.includes("/config/")) {
      newPath = currentPath
        .replace(/\/config\/\d+/, `/config/${newConfigId}`)
        .replace(/\/asset\/\d+/, "");
    } else {
      newPath = `${currentPath}/config/${newConfigId}`;
    }

    props.push(`${newPath}${getQueryParams()}`);
  }

  async function updateAllConfigNames(name) {
    const {organisationId, definitionId, configId} = props.params;
    props.updateAllConfigNames(
      organisationId,
      definitionId,
      configId,
      name,
      true,
    );
  }

  async function updateConfigIsArchived(isArchived) {
    const {organisationId, definitionId, configId} = props.params;

    await props.updateConfigisArchived(
      organisationId,
      definitionId,
      configId,
      isArchived,
    );

    const definitionPath = props.location.pathname.split("/config/")[0];

    if (isArchived) {
      const currentDefinition = props.definitions.find(
        def => def.id === Number(definitionId),
      );
      const currentConfig = currentDefinition?.workflow_instances?.find(
        config => config.id === Number(configId),
      );
      const currentGroupId =
        currentConfig?.group_id || currentConfig?.id.toString();

      const nextConfig = currentDefinition?.workflow_instances?.find(config => {
        const configGroupId = config.group_id || config.id.toString();
        return (
          configGroupId === currentGroupId &&
          !config.is_archived &&
          config.id !== Number(configId)
        );
      });

      if (nextConfig) {
        props.push(
          `${definitionPath}/config/${nextConfig.id}${getQueryParams()}`,
        );
        return;
      }
    }

    props.push(`${definitionPath}${getQueryParams()}`);
  }

  async function updateConfigIsPublic(isPublic) {
    const {organisationId, definitionId, configId} = props.params;

    props.updateConfigIsPublic(
      organisationId,
      definitionId,
      configId,
      isPublic,
    );
  }

  async function updateConfigDefaultAssetName(defaultAssetName: string) {
    const {organisationId, configId} = props.params;

    props.updateConfigDefaultAssetName(
      organisationId,
      configId,
      defaultAssetName,
    );
  }

  async function materialiseDataAsset(
    assetName: string,
    latestConfigId?: DefinitionConfig,
    materialiseWithLatestDependencies?: boolean,
  ) {
    const {organisationId, configId} = props.params;
    if (projectId && documentId) {
      const fetchGroupRuns = Boolean(latestConfigId);
      if (fetchGroupRuns) {
        setShowGroupRuns(fetchGroupRuns);
      }

      await props
        .generateDataAsset(
          organisationId,
          projectId,
          documentId,
          configId,
          assetName,
          latestConfigId,
          materialiseWithLatestDependencies,
        )
        .then(() => {
          fetchConfigRuns(true, fetchGroupRuns || undefined);
        });
    }
  }

  const addTestHandler = (dataKey: DataKey, key: string) => {
    const {organisationId} = props.params;
    const nameKey = selectedLlmRun?.name ?? null;
    props.addTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      selectedLlmRun.links,
      dataKey,
      key,
      nameKey,
    );
  };

  const updateTestHandler = (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => {
    const {organisationId} = props.params;

    props.updateTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      testId,
      testType,
      value,
    );
  };

  const deleteTestHandler = (testId: TestId) => {
    const {organisationId} = props.params;

    props.deleteTest(
      organisationId,
      selectedLlmRun.workflow_context_id,
      testId,
    );
  };

  const rerunTestsHandler = async (runId: LlmRunId) => {
    const {organisationId} = props.params;

    setRerunningTests(true);
    try {
      await props.rerunTests(organisationId, runId);
    } finally {
      setRerunningTests(false);
    }

    refreshRunHandler(organisationId, runId);
  };

  const refreshRunHandler = async (organisationId, runId) => {
    setRefreshingRun(true);
    try {
      await props.fetchLlmRun(organisationId, runId);
    } finally {
      setRefreshingRun(false);
    }
  };

  const rerunAllTestsHandler = async (runIds: LlmRunId[]) => {
    const {organisationId} = props.params;
    setRerunningTests(true);
    try {
      await props.rerunAllTests(organisationId, runIds);
    } finally {
      setRerunningTests(false);
    }
  };

  const getTestResultsHandler = (dataKey: DataKey, key: string) => {
    const {organisationId} = props.params;

    props.getTestResults(organisationId, selectedLlmRun.id, dataKey, key);
  };

  useEffect(() => {
    if (canFetchRuns()) {
      fetchConfigRuns(false);
    }
  }, [
    props.params.definitionId,
    props.params.configId,
    projectId,
    documentId,
    maxRuns,
  ]);

  useEffect(() => {
    if (!canFetchRuns() || !autoRefreshEnabled) {
      return;
    }

    const refreshInterval = setInterval(
      checkAndRefreshRuns,
      REFRESH_INTERVAL_MS,
    );
    return () => clearInterval(refreshInterval);
  }, [
    props.params.definitionId,
    props.params.configId,
    projectId,
    documentId,
    showGroupRuns,
    autoRefreshEnabled,
  ]);

  setTitle("Data Assets Create/View");
  return (
    <DataAssetsCreateView
      {...props}
      createDefinitionConfig={createDefinitionConfig}
      updateAllConfigNames={updateAllConfigNames}
      materialiseDataAsset={materialiseDataAsset}
      fetchConfigRuns={fetchConfigRuns}
      isLoadingConfigRuns={loadingConfigRuns}
      isRefreshingRuns={isRefreshing}
      fetchConfigRunsError={fetchConfigRunsError}
      clearConfigRuns={clearConfigRuns}
      updateConfigIsArchived={updateConfigIsArchived}
      clearAssetPath={clearAssetPath}
      updateConfigIsPublic={updateConfigIsPublic}
      updateConfigDefaultAssetName={updateConfigDefaultAssetName}
      onAddTest={addTestHandler}
      onUpdateTest={updateTestHandler}
      onDeleteTest={deleteTestHandler}
      rerunTests={rerunTestsHandler}
      rerunAllTests={rerunAllTestsHandler}
      getTestResults={getTestResultsHandler}
      fetchLlmRun={props.fetchLlmRun}
      selectedLlmRun={selectedLlmRun}
      showGroupRuns={showGroupRuns}
      setShowGroupRuns={handleShowGroupRunsChecked}
      updateValues={updateValues}
      isRefreshingRun={refreshingRun}
      isRerunningTests={rerunningTests}
    />
  );
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        updateAllConfigNames: updateWorkflowInstanceName(requestor),
        updateConfigisArchived: updateWorkflowInstanceIsArchived(requestor),
        updateConfigIsPublic: updateWorkflowInstanceIsPublic(requestor),
        updateConfigDefaultAssetName: updateWorkflowInstanceDefaultAssetName(
          requestor,
        ),
        addDefinitionConfig: addWorkflowInstance(requestor),
        generateDataAsset: runGenerateDataAsset(requestor),
        fetchDataAssetConfigRuns: fetchDataAssetConfigRuns(requestor),
        fetchLlmRun: llmRunFetchAction(requestor),
        clearLlmRuns,
        addTest: addTestAction(requestor),
        updateTest: updateTestAction(requestor),
        deleteTest: deleteTestAction(requestor),
        rerunTests: rerunTestsAction(requestor),
        rerunAllTests: rerunAllTestsAction(requestor),
        getTestResults: getTestResultsAction(requestor),
        push,
      },
      dispatch,
    ),
  };
}

function select(state) {
  return {
    organisation: state.organisation,
    user: state.user,
    definitions: state.workflows,
    configRuns: state.llmRuns,
    selectedLlmRun: state.llmRun,
  };
}

export default connect(
  select,
  mapDispatchToProps,
)(DataAssetsCreateViewContainer);
